/**
 * SmartTvAdManager handles ad cue point management, midroll ad transitions,
 * seek handling, and ad break state for Smart TV environments using IMA CSAI.
 * It manages watched/unwatched ad cues, coordinates player detach/restore for ad breaks,
 * and emits ad-related events for integration with the player.
 */

import { Logger, EventEmitter, PlatformUtil } from '@quickplay/foundation';
import { getAdfrequencyController } from '@quickplay/player';
import {
  Player,
  AdBreakInfo,
  ImaCsaiSmartTvPlaybackConfig,
  BUFFERING_STATES,
  AdEvent,
} from '@quickplay/player-interface';
// Delay before transitioning to ad cue point
// This value is the default unless `smartTvAdPlaybackOptions.adCueTransitionDelay` is set
const AD_CUE_TRANSITION_DELAY_MS = 1000;

const AD_CUE_PROXIMITY_CHECKER_MS = 500;
/**
 * Threshold (in seconds) to determine if a seek should snap to a nearby ad cue point.
 *
 * This is critical for maintaining correct ad behavior with IMA CSAI on Smart TVs,
 * especially on lower-end or older models where consecutive ad breaks can cause playback failures.
 *
 * IMA typically preloads ads ~7 seconds before the actual cue time.
 * For example, if there's an unwatched cue at 420s and the user seeks to 419s,
 * IMA may not detect this as a valid ad break, especially during non-linear seeks.
 *
 * To handle this gracefully, we enforce a proximity threshold — if a seek lands within
 * (-10) seconds of an unwatched cue, we adjust the playhead forward to align with that cue.
 * This ensures midrolls are reliably triggered and avoids missed ad breaks or consecutive ad failures.
 */
const AD_CUE_PROXIMITY_THRESHOLD_SEC = 10;

export class SmartTvAdManager {
  private contentProgressCheckInterval: any = null;
  private resumeSeekRetryTimer: any = null;
  private resumeSeekRetryCount = 0;
  private adCuePoints_: number[] = [];
  private watchedAdCues: Set<number> = new Set();
  private isAllAdsCompleted = false;
  private _isSeeking = false;
  private seekTarget = -1;
  private isSeekingPastAdCue = false;
  private activeAdBreakInfo: AdBreakInfo | undefined;
  private isContentPlayerActive = true;
  private previousCurrentTime = 0;
  private contentToAdTransitionDelay = 0;
  private activeAdCue: number | null = null;
  private preloadResumeTarget: number | null = null;
  private isAdCueTransitionInProgress = false;

  private get adCueTransitionDelayMs(): number {
    const configuredDelay = this.smartTvAdPlaybackOptions?.adCueTransitionDelayMs;
    return typeof configuredDelay === 'number' && Number.isFinite(configuredDelay) && configuredDelay > 0
      ? configuredDelay
      : AD_CUE_TRANSITION_DELAY_MS;
  }

  constructor(
    private contentPlayer: Player,
    private videoElement: HTMLVideoElement,
    private smartTvAdPlaybackOptions: ImaCsaiSmartTvPlaybackConfig,
    private eventEmitter: EventEmitter,
    private logger?: Logger,
  ) {
    this.contentToAdTransitionDelay = this.smartTvAdPlaybackOptions?.contentToAdTransitionDelay || 0;
  }

  public set adCuePoints(adCuePoints: number[]) {
    this.adCuePoints_ = adCuePoints;
    this.publishUnwatchedCuePoints();
  }

  public get adCuePoints(): number[] {
    return this.adCuePoints_;
  }

  public get unwatchedCuePoints(): number[] {
    return (this.adCuePoints_ ?? []).filter(cuePoint => !this.watchedAdCues.has(cuePoint));
  }

  public publishUnwatchedCuePoints(): void {
    this.eventEmitter.publish(AdEvent.ADVERT_CUEPOINTS_UPDATED, this.unwatchedCuePoints, this.adCueTransitionDelayMs);
  }

  public get isSeeking(): boolean {
    return this._isSeeking;
  }

  public set isSeeking(value: boolean) {
    this._isSeeking = value;
  }

  public get isMidrollAd(): boolean {
    return this.isSeekingPastAdCue;
  }

  /**
   * Marks an ad cue as watched. Called when an ad break starts.
   * Prevents re-triggering of ads at the same cue point during future seeks.
   */
  public addWatchedCuePoint(cuePoint: number | undefined): void {
    this.logger?.log(`[IMA CSAI][SmartTV] Ad started at cue point ${cuePoint}s`);
    const activeAdCue = cuePoint ?? this.activeAdCue;
    this.logger?.log(`[IMA CSAI][SmartTV] Ad started at current active cue point ${activeAdCue}s`);
    if (activeAdCue !== null && activeAdCue !== undefined) {
      if (!this.watchedAdCues.has(activeAdCue)) {
        this.watchedAdCues.add(activeAdCue);
        this.publishUnwatchedCuePoints();
      }
      this.activeAdCue = null;
    }
    this.isAdCueTransitionInProgress = false;
  }

  /**
   * Called when an ad is loaded and ready to play.
   * Starts tracking content progress to manage ad transitions.
   * @param activeAdBreakInfo Information about the active ad break.
   */

  public onAdLoadedEvent(activeAdBreakInfo: AdBreakInfo): void {
    this.startContentProgressTracking(activeAdBreakInfo);
  }

  /**
   * Handles the end of an ad end event.
   * This is called when an ad break ends/ad failed/ad skipped, allowing the content to resume playback.
   */
  public onAdEventEnd(): void {
    this.handleContentResume();
  }

  /**
   * on Player stopped event.
   * resets the Smart TV ad manager state.
   */
  public onStoppedEvent(): void {
    this.reset();
  }

  /**
   * Returns a single cue point when resuming playback:
   * - If resumeTime is near an ad cue (within proximity), return that cue.
   * - Otherwise, always return 0 (assume preroll only).
   *
   * For Smart TV platforms.
   *
   * @param resumeTime - Resume time after preroll (in seconds)
   * @returns Cue point in seconds
   */
  public getRelevantCuePointOnResume(resumeTime: number): number {
    const cuePoints = this.adCuePoints_;
    const nearWindow = AD_CUE_PROXIMITY_THRESHOLD_SEC;

    if (!cuePoints?.length) return 0;

    for (let i = 1; i < cuePoints.length; i++) {
      const cue = cuePoints[i];

      // If resumeTime is within [cue - nearWindow, cue]
      if (resumeTime >= cue - nearWindow && resumeTime <= cue) {
        return cue;
      }
    }

    // Not close to any cue → default to preroll
    return cuePoints[0];
  }

  /**
   * Marks all cue points as watched if they are at or before the given playback time.
   *
   * This method is essential for continue watching sessions on Smart TVs, where users
   * may resume content at a specific time after having previously watched ads.
   * By marking ads as "watched" up to the resume point, we prevent re-triggering
   * of ads that were already shown in a previous viewing session.
   *
   * For example, if a user resumes at 300 seconds and there were ad breaks at
   * 0s, 120s, and 240s, this method will mark all three as watched to avoid
   * replaying those ads during the current session.
   *
   * Updates the internal watchedAdCues set to maintain ad state consistency
   * throughout the playback session.
   *
   * @param playbackTime - The initial or resume time in seconds. All ad cue points
   *                       at or before this time will be marked as watched.
   */
  public markWatchedCuesUpTo(playbackTime: number): void {
    let hasChanged = false;
    for (const cue of this.adCuePoints_) {
      if (cue <= playbackTime && !this.watchedAdCues.has(cue)) {
        this.watchedAdCues.add(cue);
        hasChanged = true;
      }
    }
    if (hasChanged) this.publishUnwatchedCuePoints();
  }

  /**
   * Returns true if the resume time is between the preroll and first midroll,
   * and not near the first midroll cue (within the ad proximity threshold).
   *
   * Useful to skip IMA player recreation during continue-watching flows.
   *
   * @param resumeTime - Time (in seconds) where content playback resumes
   * @returns true if safe to skip IMA recreation, false otherwise
   */
  public isPlaybackInEarlyContentRange(resumeTime: number): boolean {
    if (!this.adCuePoints_ || this.adCuePoints_.length < 2) return true; // fallback safe

    const firstMidroll = this.adCuePoints_[1];

    // Resume is after preroll and before "near" first midroll
    return resumeTime > this.adCuePoints_[0] && resumeTime < firstMidroll - AD_CUE_PROXIMITY_THRESHOLD_SEC;
  }
  /**
   * Handles seek logic while respecting ad cue points.
   * Ensures seek doesn't skip unwatched ads by checking if a cue exists between
   * the current and target positions and adjusting the seek accordingly.
   */
  public async seek(seekPoint: number): Promise<void> {
    if (this.isSeekingPastAdCue) {
      this.logger?.debug('[IMA CSAI][SmartTV] Seek attempt ignored due to its already past ad cue.');
      return;
    }

    if (!this.shouldUnloadForAdBreak()) {
      await this.handleSeekWithSuppressedAds(seekPoint);
      return;
    }

    this.seekTarget = seekPoint;
    this.isSeeking = true;
    this.isSeekingPastAdCue = false;

    if (this.isContentPlayerActive) {
      this.previousCurrentTime = this.videoElement.currentTime;
    }

    await this.stopContentProgressTracking();

    this.logger?.debug(`[IMA CSAI][SmartTV] Current time: ${this.previousCurrentTime}s, Seek target: ${seekPoint}s`);

    const isSeekingForward = seekPoint >= this.previousCurrentTime;

    const adCueBetween = isSeekingForward
      ? this.getClosestUnwatchedCueForward(this.previousCurrentTime, seekPoint)
      : this.getClosestUnwatchedCueNearOrBefore(seekPoint);

    if (adCueBetween === undefined) {
      this.logger?.debug(
        `[IMA CSAI][SmartTV] No unwatched ad cue found, proceeding with normal seek to ${seekPoint}s and`,
      );
      await this.performSeek(seekPoint);
      return;
    }

    this.logger?.debug(
      `[IMA CSAI][SmartTV] ad cue found, proceeding with normal seek to ${adCueBetween}s and seekpoint ${seekPoint}s`,
    );

    this.isSeekingPastAdCue = true;

    const shouldAdjustSeek = Math.abs(seekPoint - adCueBetween) <= AD_CUE_PROXIMITY_THRESHOLD_SEC;

    this.logger?.debug(
      `[IMA CSAI][SmartTV] Should adjust seek: ${shouldAdjustSeek}, ad cue at ${adCueBetween}s, seek point at ${seekPoint}s`,
    );
    this.seekTarget = shouldAdjustSeek ? adCueBetween : seekPoint;
    // Dev Note: Setting activeAdCue here ensures correct ad tracking during the seek transition.
    this.activeAdCue = adCueBetween;

    this.logger?.debug(
      `[IMA CSAI][SmartTV] Seek crossed or approached unwatched ad cue — flagging for IMA handling at ${this.seekTarget}s`,
    );
    await this.performSeek(this.seekTarget);
  }

  private shouldUnloadForAdBreak(): boolean {
    return getAdfrequencyController().evaluateOpportunity();
  }

  private async handleSeekWithSuppressedAds(seekPoint: number): Promise<void> {
    this.logger?.debug(
      `[IMA CSAI][SmartTV] Seek to ${seekPoint}s proceeding normally due to suppressing ad break based on afc decisions`,
    );
    if (this.isContentPlayerActive) {
      this.previousCurrentTime = this.videoElement.currentTime;
    }
    this.markWatchedCuesUpTo(seekPoint);
    await this.handleNormalSeek(seekPoint);
  }

  /**
   * Returns the nearest upcoming cue point from the current time.
   * For example, if currentTime is 593 and cue points include [0, 600, 1200], returns 600.
   * @param currentTime - The current playback time in seconds
   * @returns The nearest upcoming cue point, or undefined if none exists
   */
  public getNearestUpcomingCue(currentTime: number): number | undefined {
    const nearestCue = this.adCuePoints_.find(cue => cue >= currentTime);
    if (nearestCue !== undefined && this.activeAdCue === null) {
      this.logger?.log(`[IMA CSAI][SmartTV] Ad started at Nearest cue point ${nearestCue}s`);
      this.activeAdCue = nearestCue;
    }
    return nearestCue;
  }

  /**
   * Finds the closest unwatched ad cue forward between current time and seek target.
   * Also includes cues shortly after the seek point within the ad proximity threshold.
   */
  private getClosestUnwatchedCueForward(currentTime: number, seekPoint: number): number | undefined {
    const forwardCues = this.adCuePoints_.filter(cue => cue > currentTime && cue <= seekPoint);

    // If the last cue in the range is watched, skip checking earlier ones
    const lastCueInRange = forwardCues[forwardCues.length - 1];
    if (lastCueInRange !== undefined && this.watchedAdCues.has(lastCueInRange)) {
      this.logger?.debug(
        `[IMA CSAI][SmartTV] Last forward cue (${lastCueInRange}s) already watched — skipping older cues.`,
      );
      return undefined;
    }

    // Else: pick the last unwatched cue in range or just after seek point (within threshold)
    const candidates = this.adCuePoints_.filter(
      cue =>
        !this.watchedAdCues.has(cue) &&
        ((cue > currentTime && cue <= seekPoint) ||
          (seekPoint < cue && cue - seekPoint <= AD_CUE_PROXIMITY_THRESHOLD_SEC) ||
          cue === seekPoint),
    );

    return candidates.sort((a, b) => b - a)[0];
  }

  /**
   * Finds the closest unwatched ad cue at or before the target seek time.
   *
   * FEATURES:
   * - Triggers an ad if within 10s proximity of an unwatched future cue (forward seeks)
   * - Avoids triggering if seek lands between two watched cues
   * - Prevents retriggering older ads on backward seeks
   * - Handles all edge cases for both forward and backward seeking
   *
   * LOGIC FLOW:
   * Case 1: Skip if between two watched cues or after last watched cue
   * Case 2: Trigger unwatched future cue if within proximity
   * Case 3: Trigger closest past unwatched cue (with backward seek protection)
   */
  private getClosestUnwatchedCueNearOrBefore(targetTime: number): number | undefined {
    const adCues = this.adCuePoints_;
    const watched = this.watchedAdCues;

    if (!adCues.length) return undefined;

    const previousCue = adCues.filter(cue => cue <= targetTime).sort((a, b) => b - a)[0];
    const nextCue = adCues.find(cue => cue > targetTime);
    const lastCue = adCues[adCues.length - 1];

    // Case 1: Skip if between two watched cues or after last watched cue
    const isBetweenTwoWatchedCues =
      previousCue !== undefined && nextCue !== undefined && watched.has(previousCue) && watched.has(nextCue);

    const isAfterLastWatchedCue = targetTime > lastCue && watched.has(lastCue);

    if (isBetweenTwoWatchedCues || isAfterLastWatchedCue) {
      this.logger?.debug(
        `[IMA CSAI] Seek landed between watched cues (${previousCue}, ${nextCue}) or after last watched cue — no ad triggered.`,
      );
      return undefined;
    }

    // Case 2:  Backward seek - trigger unwatched cue within 10s proximity, return the upcoming cue point
    const futureProximityCue = adCues.find(
      cue => cue > targetTime && cue - targetTime <= AD_CUE_PROXIMITY_THRESHOLD_SEC && !watched.has(cue),
    );

    if (futureProximityCue !== undefined) {
      this.logger?.debug(`[IMA CSAI] Seek near future cue (${futureProximityCue}) within proximity — triggering.`);
      return futureProximityCue;
    }

    // Case 3: Backward seek - prevent retriggering old cues
    // Find the most recent watched cue at or before the target time
    const nearestWatchedCue = adCues.filter(cue => cue <= targetTime && watched.has(cue)).sort((a, b) => b - a)[0];

    // Only consider past cues that are after the nearest watched cue
    const fallbackCue = adCues
      .filter(
        cue => cue <= targetTime && !watched.has(cue) && (nearestWatchedCue === undefined || cue > nearestWatchedCue),
      )
      .sort((a, b) => b - a)[0];

    if (fallbackCue !== undefined) {
      this.logger?.debug(`[IMA CSAI] Seek hit unwatched past cue (${fallbackCue}) — triggering.`);
    }

    return fallbackCue;
  }

  /**
   * Main seek executor. Delegates to either ad cue seek or normal seek flow.
   * If an error occurs, safely resumes content playback to avoid user disruption.
   */
  private async performSeek(seekPoint: number | undefined): Promise<void> {
    if (seekPoint === undefined || seekPoint === null) return;

    try {
      if (this.isSeekingPastAdCue) {
        await this.handleSeekPastAdCue(seekPoint);
      } else {
        await this.handleNormalSeek(seekPoint);
      }
    } catch (error) {
      this.logger?.error('[IMA CSAI][SmartTV] Error during seek:', error);
      await this.handleContentResume();
    }
  }

  /**
   * When seeking into or near an unwatched ad cue, unloads the content player
   * and sets videoElement's currentTime directly. Used for IMA-driven ad handling.
   */
  private async handleSeekPastAdCue(seekPoint: number): Promise<void> {
    this.logger?.debug(
      `[IMA CSAI][SmartTV] Detaching content player for seek' ${seekPoint} content to ad delay ${this.contentToAdTransitionDelay}`,
    );
    //Note: Emitting the buffering event while content is unloaded.
    this.eventEmitter.publish('bufferingstatechanged', BUFFERING_STATES.ACTIVE);

    this.preloadResumeTarget = seekPoint > 0 ? seekPoint : this.previousCurrentTime;
    this.isContentPlayerActive = false;

    if (PlatformUtil.isWPE()) {
      // STB/WPE path: seek must happen BEFORE unload.
      // Why: on some STB WebKit stacks (WPE/XUMO), unload detaches media and drops
      // readyState to HAVE_NOTHING; any later currentTime assignment can be ignored.
      // Result without this ordering: IMA does not observe the cue/seek edge and
      // midroll transition can fail or resume from the wrong position.
      this.videoElement.currentTime = seekPoint;

      // STB timing guard: allow a small configurable settle window so the seek is
      // committed before unload on slower/older devices.
      await new Promise<void>(resolve => setTimeout(resolve, this.contentToAdTransitionDelay));

      await this.contentPlayer.unloadAndSavePreload();
      this.isContentPlayerActive = false;
    } else {
      // Non-WPE STB/web path: unload first, then apply seek time.
      // Why: these engines reliably accept currentTime after unload, and this
      // keeps legacy transition ordering that is stable on Tizen/LG/WebOS flows.
      await this.contentPlayer.unloadAndSavePreload();
      this.isContentPlayerActive = false;
      setTimeout(() => {
        this.videoElement.currentTime = seekPoint;
      }, this.contentToAdTransitionDelay);
    }
  }

  /**
   * Performs a standard seek operation through the content player API.
   * Applicable when not crossing any active or pending ad cue point.
   */
  private async handleNormalSeek(seekPoint: number): Promise<void> {
    if (this.isContentPlayerActive) {
      this.logger?.debug('[IMA CSAI][SmartTV] Normal seek point', seekPoint);
      this.contentPlayer.seek(seekPoint);
      this.resetSeek();
    }
  }

  /**
   * Handles transitioning to an ad cue. Unloads content and sets playhead
   * directly to the ad cue time. Marks ad break as started.
   */
  private async handleAdCuePointTransition(cuePoint: number, activeAdBreakInfo: AdBreakInfo): Promise<void> {
    if (this.isAdCueTransitionInProgress) {
      this.logger?.debug(
        `[IMA CSAI][SmartTV] Skipping ad cue transition at ${cuePoint}s — a transition is already in progress`,
      );
      return;
    }
    this.isAdCueTransitionInProgress = true;

    this.logger?.debug(
      `[IMA CSAI][SmartTV] Transitioning to ad cue at ${cuePoint}s ${activeAdBreakInfo} contenttoaddelay${this.contentToAdTransitionDelay}`,
    );
    this.preloadResumeTarget = this.previousCurrentTime > 0 ? this.previousCurrentTime : cuePoint;
    this.isContentPlayerActive = false;

    //Note: Emitting the buffering event while content is unloaded.
    this.eventEmitter.publish('bufferingstatechanged', BUFFERING_STATES.ACTIVE);

    if (PlatformUtil.isWPE()) {
      // STB/WPE path: set cue time BEFORE unload for the same reason as seek-past-cue flow.
      // On affected STB engines, setting currentTime after unload may be ignored, which
      // prevents IMA from seeing cue entry and can break ad start/resume transitions.
      this.activeAdCue = cuePoint;
      this.videoElement.currentTime = cuePoint;

      // STB timing guard: preserve ordering by waiting briefly so cue seek is observed
      // before detach happens on low-performance devices.
      await new Promise<void>(resolve => setTimeout(resolve, this.contentToAdTransitionDelay));

      try {
        await this.contentPlayer.unloadAndSavePreload();
        this.isContentPlayerActive = false;
      } catch (error) {
        this.logger?.error(`[IMA CSAI][SmartTV] Failed to unload and save preload for cue ${cuePoint}s`, error);
        this.isAdCueTransitionInProgress = false;
        this.activeAdCue = null;
        this.handleContentResume();
        return;
      }

      this.seekTarget = -1;
    } else {
      // Non-WPE SmartTV/web path: unload first, then apply cue time.
      // These platforms reliably retain/accept post-unload currentTime assignment.
      await this.contentPlayer.unloadAndSavePreload();
      this.isContentPlayerActive = false;
      setTimeout(() => {
        this.activeAdCue = cuePoint;
        this.videoElement.currentTime = cuePoint;
        this.seekTarget = -1;
      }, this.contentToAdTransitionDelay);
    }
  }

  /**
   * Starts polling for cue proximity to trigger mid-roll transitions.
   * Runs periodically and checks if current time is approaching any unwatched cue.
   */
  private async startContentProgressTracking(activeAdBreakInfo: AdBreakInfo): Promise<void> {
    if (this.isSeeking) return;

    this.activeAdBreakInfo = activeAdBreakInfo;
    this.isAllAdsCompleted = false;

    if (this.contentProgressCheckInterval) clearInterval(this.contentProgressCheckInterval);

    this.handleLinearPlaybackAdTransition();
  }

  /**
   * Interval checker that determines if the playhead is close to any upcoming ad cue.
   * Triggers ad transition if proximity is met and ad has not been watched.
   */
  private handleLinearPlaybackAdTransition(): void {
    const delay = this.adCueTransitionDelayMs;

    this.contentProgressCheckInterval = setInterval(() => {
      // Skip this tick — state is temporarily invalid but will recover
      if (!this.isContentPlayerActive || this.isAllAdsCompleted || this.isSeeking) return;

      // Player is gone entirely — interval should not exist, shut it down
      if (!this.contentPlayer) {
        this.stopContentProgressTracking();
        return;
      }

      const currentTime = this.contentPlayer.currentTime;
      this.previousCurrentTime = currentTime;
      const approachingAdCue = this.adCuePoints_.find(cue => {
        const timeToCue = cue - currentTime;
        return timeToCue > 0 && timeToCue <= delay / 1000 && !this.watchedAdCues.has(cue);
      });

      this.handleApproachingAdCue(approachingAdCue);
    }, AD_CUE_PROXIMITY_CHECKER_MS);
  }

  private handleApproachingAdCue(cuePoint: number | undefined): void {
    if (cuePoint === undefined) return;
    if (!this.shouldUnloadForAdBreak()) {
      this.logger?.debug(
        `[IMA CSAI][SmartTV] Skipping ad cue transition at ${cuePoint}s because ad cooldown is active`,
      );
      if (!this.watchedAdCues.has(cuePoint)) {
        this.watchedAdCues.add(cuePoint);
        this.publishUnwatchedCuePoints();
      }
      return;
    }

    if (this.activeAdBreakInfo) {
      void this.handleAdCuePointTransition(cuePoint, this.activeAdBreakInfo);
    }
  }

  /**
   * Resumes content after ad break or interrupted seek. Optionally seeks to stored target.
   * Restores preloaded content player and resets seek flags.
   */
  private handleContentResume(): void {
    this.logger?.debug('[IMA CSAI][SmartTV] Resuming content after ad break');

    const isWpe = PlatformUtil.isWPE();

    if (isWpe) {
      this.clearResumeSeekRetryTimer();
    }
    if (!this.isContentPlayerActive) {
      this.contentPlayer.restoreFromPreload();
    }

    setTimeout(() => {
      const resumeTarget = this.seekTarget > -1 ? this.seekTarget : this.preloadResumeTarget;

      const shouldSeek = Number.isFinite(resumeTarget) && (resumeTarget as number) > 0;

      if (shouldSeek) {
        const target = resumeTarget as number;

        this.contentPlayer.seek(target);

        this.startWpeResumeSeekRetry(target);

        this.logger?.debug(`[IMA CSAI][SmartTV] Resume seek applied at ${target}s`);
      }

      this.contentPlayer.play();

      if (this.isSeeking) {
        this.resetSeek();
      }

      this.seekTarget = -1;
      this.preloadResumeTarget = null;
      this.isContentPlayerActive = true;
      this.activeAdBreakInfo = undefined;

      this.stopContentProgressTracking();
    }, 0);
  }
  /**
   * Stops the content progress tracking interval.
   * Clears the interval if it exists to prevent memory leaks.
   */
  private async stopContentProgressTracking(): Promise<void> {
    if (this.contentProgressCheckInterval) {
      clearInterval(this.contentProgressCheckInterval);
      this.contentProgressCheckInterval = null;
    }
  }

  /**
   * WPE recovery helper: re-applies resume seek for a short window because
   * some devices can ignore early seek calls during restore/load.
   */
  private startWpeResumeSeekRetry(resumeTarget: number): void {
    this.resumeSeekRetryCount = 0;
    const maxRetries = 8;
    const retryIntervalMs = 250;

    this.resumeSeekRetryTimer = setInterval(() => {
      this.resumeSeekRetryCount += 1;
      const observedTime = Math.max(this.contentPlayer.currentTime || 0, this.videoElement.currentTime || 0);

      const isAtOrPastTarget = observedTime >= resumeTarget - 1;
      if (isAtOrPastTarget) {
        this.clearResumeSeekRetryTimer();
        this.logger?.debug(
          `[IMA CSAI][SmartTV] Resume seek stabilized at ${observedTime}s for target ${resumeTarget}s`,
        );
        return;
      }

      this.contentPlayer.seek(resumeTarget);
      this.logger?.debug(
        `[IMA CSAI][SmartTV] Resume seek applied at ${resumeTarget}s (retry ${this.resumeSeekRetryCount}/${maxRetries})`,
      );

      if (this.resumeSeekRetryCount >= maxRetries) {
        this.clearResumeSeekRetryTimer();
        this.logger?.warn(
          `[IMA CSAI][SmartTV] Resume seek did not stabilize on WPE after ${maxRetries} attempts. Last observed time: ${observedTime}s, target: ${resumeTarget}s`,
        );
      }
    }, retryIntervalMs);
  }

  /**
   * Clears the active WPE resume-seek retry timer, if any.
   */
  private clearResumeSeekRetryTimer(): void {
    if (this.resumeSeekRetryTimer) {
      clearInterval(this.resumeSeekRetryTimer);
      this.resumeSeekRetryTimer = null;
    }
    this.resumeSeekRetryCount = 0;
  }

  /**
   * Resets the seek state.
   */
  private resetSeek(): void {
    this.isSeeking = false;
    this.isSeekingPastAdCue = false;
    this.previousCurrentTime = 0;
  }

  /**
   * Resets the Smart TV ad manager state.
   * Clears all ad cues, watched cues, and stops content progress tracking.
   * Resets seek state and active ad break info.
   */
  private reset(): void {
    this.stopContentProgressTracking();
    this.clearResumeSeekRetryTimer();
    this.watchedAdCues.clear();
    this.resetSeek();
    this.seekTarget = -1;
    this.preloadResumeTarget = null;
    this.isContentPlayerActive = true;
    this.adCuePoints = [];
    this.activeAdBreakInfo = undefined;
    this.isAdCueTransitionInProgress = false;
    this.logger?.debug('[IMA CSAI][SmartTV] Reset state');
  }
}
